import { faCircle, faExternalLinkAlt, faFileMedical } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { memo, useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import * as timeago from 'timeago.js';

import AppointmentWithinAvailability from '@/components/AppointmentWithinAvailability';
import DrugScreenWarningModal from '@/components/DrugScreenWarningModal';
import { Color } from '@/constants/color';
import AppointmentAttendancePills from '@/ui/components/AppointmentAttendancePills';
import ListCard from '@/ui/components/ListCard';
import { getDrugScreenTimeWindowsData } from '@/utils/drugScreenTimeWindows';

import Button from '../Button';
import Tooltip from '../Tooltip';

import type { Props } from './types';

/**
 * NOTE: This component is business/domain-specific and does not belong in the
 * generic UI primitives library (ui/components). It should be moved to a more
 * appropriate domain-specific location in the codebase.
 */
const AppointmentAttendanceCard = ({
  attendance,
  personTimeZone,
  showEncounterLink = false
}: Props) => {
  const navigate = useNavigate();
  const [isModalOpen, setIsModalOpen] = useState(false);

  const {
    appointment,
    appointment_within_availability,
    cancellation_reason,
    charting_completed,
    charting_status,
    drug_screen,
    feedback,
    group_enrollment,
    internal_status,
    member_id: personId
  } = attendance;
  const { id: appointmentId, healthie_id, participants, start_at, title, providers } = appointment;

  const {
    durationInMinutes,
    isDrugScreenButtonEnabled,
    isDrugScreenButtonEnabledForAdHoc,
    isInTheFuture,
    isOngoing,
    isWarningModalEnabled: shouldShowModal,
    startAt
  } = getDrugScreenTimeWindowsData(appointment);

  const shouldShowDrugScreenButton = !drug_screen && isDrugScreenButtonEnabled;
  const shouldShowDrugScreenButtonForAdHoc = !drug_screen && isDrugScreenButtonEnabledForAdHoc;
  const shouldShowDrugScreenButtons =
    shouldShowDrugScreenButton || shouldShowDrugScreenButtonForAdHoc;
  const shouldShowMeetingButtons = !!(
    isOngoing &&
    (appointment?.external_links?.zoom_start?.url || appointment?.external_links?.zoom_join?.url)
  );
  const shouldShowButtonsSection = shouldShowMeetingButtons || shouldShowDrugScreenButtons;
  // Group appointments have one encounter per attendee, so the link is only
  // unambiguous for a single-attendee appointment.
  const shouldShowEncounterLink = showEncounterLink && !!healthie_id && participants?.length === 1;

  const primaryProvider = providers?.[0];
  const homebaseUrl = appointment?.external_links?.homebase?.url;

  const closeWarningModal = useCallback(() => setIsModalOpen(false), []);

  const handleRecordDrugScreen = useCallback(() => {
    if (isModalOpen) setIsModalOpen(false);
    if (appointmentId)
      navigate(`/people/${personId}/drug-screens/new?appointment-id=${appointmentId}`, {
        relative: 'path'
      });
  }, [appointmentId, isModalOpen, navigate, personId]);

  return (
    <>
      <ListCard
        className="@container"
        debug={attendance}
        headingActions={
          <>
            {shouldShowEncounterLink && (
              <Button
                size="xs"
                color={Color.BLUE}
                onClick={() => navigate(`/encounters/appointment/by_healthie_id/${healthie_id}/`)}>
                <FontAwesomeIcon icon={faFileMedical} className="mr-1" />
                View Encounter
              </Button>
            )}
            <AppointmentWithinAvailability
              appointmentWithinAvailability={appointment_within_availability}
            />
            {homebaseUrl && (
              <Tooltip tooltipContent="Open in Homebase">
                <Button
                  ariaLabel="Open in Homebase"
                  size="xs"
                  color={Color.WHITE}
                  href={homebaseUrl}
                  target="_blank"
                  rel="noreferrer">
                  <FontAwesomeIcon icon={faExternalLinkAlt} />
                </Button>
              </Tooltip>
            )}
          </>
        }
        // Narrow cards (e.g. the extension side panel) drop the actions to their own
        // row; wide cards float them to the right of the title.
        headingActionsClassName="flex basis-full flex-wrap items-center gap-2 mb-2 @lg:basis-auto @lg:mb-1 @lg:ml-auto @lg:self-center"
        headingHref={`/people/${personId}/appointments/${appointment.id}`}
        title={`${title} (${durationInMinutes}min) ${isOngoing ? ' - Ongoing' : ''}`}
        {...(isOngoing && {
          headingIcon: faCircle,
          headingIconClassName: 'text-green-500',
          headingIconTooltip: `This meeting is ongoing and started (${timeago.format(startAt)})`
        })}>
        <div className="flex flex-wrap gap-1">
          <AppointmentAttendancePills
            status={internal_status}
            cancellationReason={cancellation_reason}
            dateTime={start_at ? new Date(start_at) : undefined}
            memberTZ={personTimeZone}
            provider={primaryProvider}
            appointment={appointment}
            groupEnrollment={group_enrollment}
            rescheduledBySource={appointment.rescheduled_by_source}
            autoCancelEligible={attendance.auto_cancel_eligible}
            chartingCompleted={charting_completed}
            chartingStatus={charting_status}
            drugScreen={drug_screen}
            feedback={feedback}
          />
          {/* Buttons */}
          {shouldShowButtonsSection && (
            <div className="border-gray-200 border-solid border-t mt-2 w-full">
              {/* Meeting */}
              {shouldShowMeetingButtons && (
                <div className="flex gap-2 mt-2">
                  {/* Join as host */}
                  {appointment?.external_links?.zoom_start?.url && (
                    <Button
                      color={Color.WHITE}
                      href={appointment.external_links.zoom_start.url}
                      size="xs"
                      rel="noreferrer"
                      target="_blank">
                      Join as host
                    </Button>
                  )}

                  {/* Join as guest */}
                  {appointment?.external_links?.zoom_join?.url && (
                    <Button
                      color={Color.WHITE}
                      href={appointment.external_links.zoom_join.url}
                      size="xs"
                      rel="noreferrer"
                      target="_blank">
                      Join as guest
                    </Button>
                  )}
                </div>
              )}

              {/* Drug screen */}
              {shouldShowDrugScreenButton && (
                <Button
                  className="mt-2"
                  size="xs"
                  onClick={shouldShowModal ? () => setIsModalOpen(true) : handleRecordDrugScreen}>
                  Record Drug Screen
                </Button>
              )}

              {shouldShowDrugScreenButtonForAdHoc && (
                <Button
                  className="mt-2"
                  color={Color.WHITE}
                  size="xs"
                  onClick={shouldShowModal ? () => setIsModalOpen(true) : handleRecordDrugScreen}>
                  Create Drug Screen
                </Button>
              )}
            </div>
          )}
        </div>
      </ListCard>

      {start_at && shouldShowDrugScreenButtons && shouldShowModal && (
        <DrugScreenWarningModal
          appointmentStartAt={startAt}
          isAppointmentInTheFuture={isInTheFuture}
          isModalOpen={isModalOpen}
          personTimeZone={personTimeZone}
          goToDrugScreenForm={handleRecordDrugScreen}
          onClose={closeWarningModal}
        />
      )}
    </>
  );
};
export default memo(AppointmentAttendanceCard);
